/**
 * Coverage tab component.
 *
 * Displays code coverage metrics for all Blooph microservices.
 *
 * Implementation plan reference: D10.6 - D10.13
 */

import { useEffect, useMemo, useState } from 'react';
import { ApiError, getCoverage } from '@/api/client';
import { getApiBaseUrl } from '@/api/baseUrl';
import { formatDateTime } from '@/utils/format';
import { POLL_INTERVAL_COSTS_MS, useRefresh } from '@/context/RefreshContext';
import type { CoverageResponse, ServiceCoverage } from '@/types/api';

type SortField = 'service' | 'lines' | 'statements' | 'functions' | 'branches';
type SortDirection = 'asc' | 'desc';

const formatWhen = (iso: string): string =>
  formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });

function coverageBadgeClass(pct: number): string {
  if (pct >= 90) return 'coverage-badge coverage-badge--good';
  if (pct >= 70) return 'coverage-badge coverage-badge--warning';
  return 'coverage-badge coverage-badge--poor';
}

function formatPct(pct: number): string {
  return `${pct.toFixed(1)}%`;
}

/** Extracts a readable message from an API error response body (JSON or plain text). */
function formatApiErrorBody(body: unknown): string | null {
  if (body === undefined || body === null) {
    return null;
  }
  if (typeof body !== 'string') {
    try {
      return JSON.stringify(body);
    } catch {
      return Object.prototype.toString.call(body);
    }
  }
  const t = body.trim();
  if (t === '') {
    return null;
  }
  try {
    const o = JSON.parse(t) as { message?: unknown; error?: unknown };
    const msg = o.message ?? o.error;
    if (typeof msg === 'string' && msg.trim() !== '') {
      return msg.trim();
    }
  } catch {
    /* not JSON */
  }
  const max = 500;
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

/**
 * When fetch() fails before an HTTP response, browsers throw TypeError with
 * engine-specific messages (e.g. Chromium: "Failed to fetch", Safari: "Load failed").
 */
function appendClientFetchFailureHint(err: Error, lines: string[]): void {
  if (err.name !== 'TypeError') {
    return;
  }
  const m = err.message.trim().toLowerCase();
  const looksLikeFetchFailure =
    m === 'failed to fetch' ||
    m === 'load failed' ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror');

  if (!looksLikeFetchFailure) {
    return;
  }

  lines.push(
    'Hint: The browser never received an HTTP response. Typical causes include API or network outage, CORS blocking ops.blooph.com, TLS/certificate issues, VPN or proxy, or the /coverage endpoint not being deployed. Check that GET /coverage is wired in API Gateway with CORS and JWT authorizer like other routes.',
  );
}

/** Builds a multi-line explanation for the Coverage tab when GET /coverage fails. */
function formatCoverageLoadError(err: unknown): string {
  const lines: string[] = [];

  try {
    lines.push(`API base: ${getApiBaseUrl()}`);
  } catch (configErr: unknown) {
    lines.push(
      `Configuration: ${configErr instanceof Error ? configErr.message : String(configErr)}`,
    );
  }

  lines.push('Request: GET /coverage');

  if (err instanceof ApiError) {
    // Gate the `HTTP status:` line on a genuine HTTP status. Client-
    // side wraps like NETWORK_ERROR (CORE-1056c helper B) set
    // status:0 to signal "no HTTP response received" — rendering
    // "HTTP status: 0" in the full-diagnostic panel would contradict
    // the deliberate `(HTTP 0)` suffix suppression in formatErrorMessage
    // and confuse an operator trying to triage. Round-2 review catch.
    if (err.status > 0) {
      lines.push(`HTTP status: ${err.status}`);
    }
    lines.push(`Details: ${err.message}`);
    const bodyText = formatApiErrorBody(err.body);
    if (bodyText !== null) {
      lines.push(`Response body: ${bodyText}`);
    }
    if (err.status === 401) {
      lines.push(
        'Hint: Session may have expired — try logging out and signing in again.',
      );
    } else if (err.status === 403) {
      lines.push('Hint: Your account may not be authorized to access coverage data from the API.');
    } else if (err.status === 404) {
      lines.push(
        'Hint: The coverage endpoint is missing on this API (deploy observability-api Stage 10 / GET /coverage).',
      );
    } else if (err.status === 503) {
      lines.push('Hint: The coverage Lambda may be unavailable; retry later.');
    } else if (err.code === 'NETWORK_ERROR') {
      // Preserve the CORS/TLS/VPN diagnostic that appendClientFetchFailureHint
      // used to emit for TypeError('Failed to fetch'). CORE-1056c helper B
      // wraps fetch throws as ApiError{status:0, code:'NETWORK_ERROR'} —
      // without this branch, the wrap would have blackholed the diagnostic
      // (helper now unreachable because the fetch-throw path stays in the
      // ApiError branch above, not the generic Error fallback below).
      lines.push(
        'Hint: The browser never received an HTTP response. Typical causes include API or network outage, CORS blocking ops.blooph.com, TLS/certificate issues, VPN or proxy, or the /coverage endpoint not being deployed. Check that GET /coverage is wired in API Gateway with CORS and JWT authorizer like other routes.',
      );
    }
    return lines.join('\n');
  }

  if (err instanceof Error) {
    lines.push(`Client error (${err.name}): ${err.message}`);
    appendClientFetchFailureHint(err, lines);
    return lines.join('\n');
  }

  lines.push(`Unknown error: ${String(err)}`);
  return lines.join('\n');
}

function CoverageCell({ pct }: { pct: number }): JSX.Element {
  return (
    <td className="coverage-cell">
      <span className={coverageBadgeClass(pct)}>{formatPct(pct)}</span>
    </td>
  );
}

function getSortValue(service: ServiceCoverage, field: SortField): number | string {
  if (field === 'service') {
    return service.displayName.toLowerCase();
  }
  if (service.metrics === null) {
    return -1; // Services without metrics go to bottom when sorting by coverage
  }
  switch (field) {
    case 'lines':
      return service.metrics.lines.pct;
    case 'statements':
      return service.metrics.statements.pct;
    case 'functions':
      return service.metrics.functions.pct;
    case 'branches':
      return service.metrics.branches.pct;
    default:
      return 0;
  }
}

export function CoverageTab(): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible } = useRefresh();
  const [data, setData] = useState<CoverageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('lines');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function runFetch(silent: boolean): void {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      void getCoverage()
        .then((response) => {
          if (!cancelled) {
            setData(response);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || silent) {
            return;
          }
          setError(formatCoverageLoadError(err));
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    runFetch(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => runFetch(true), POLL_INTERVAL_COSTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  const sortedServices = useMemo(() => {
    if (data === null) {
      return [];
    }
    return [...data.services].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      let cmp: number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal);
      } else {
        cmp = (aVal as number) - (bVal as number);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, sortField, sortDirection]);

  function handleSort(field: SortField): void {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Default to ascending for service name, ascending (worst first) for coverage metrics
      setSortDirection('asc');
    }
  }

  function sortIndicator(field: SortField): string {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' \u25B2' : ' \u25BC';
  }

  return (
    <div className="coverage-tab">
      <h2>Code Coverage</h2>
      <p className="tab-lead">
        Code coverage metrics for all Blooph microservices. Click &quot;View Report&quot; to see the full HTML coverage report.
      </p>

      {isLoading && <p className="placeholder">Loading coverage data...</p>}
      {!isLoading && error !== null && (
        <div className="coverage-error" role="alert">
          <p className="coverage-error-title">Could not load coverage data</p>
          <pre className="coverage-error-detail">{error}</pre>
        </div>
      )}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="fetched-at">Last updated {formatWhen(data.fetchedAt)}</p>

          <div className="coverage-legend">
            <span className="coverage-legend-item">
              <span className="coverage-badge coverage-badge--good">Good</span> &ge;90%
            </span>
            <span className="coverage-legend-item">
              <span className="coverage-badge coverage-badge--warning">Fair</span> 70-89%
            </span>
            <span className="coverage-legend-item">
              <span className="coverage-badge coverage-badge--poor">Poor</span> &lt;70%
            </span>
          </div>

          <div className="table-wrap">
            <table className="coverage-table">
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className="sort-header"
                      onClick={(): void => handleSort('service')}
                    >
                      Service{sortIndicator('service')}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="sort-header"
                      onClick={(): void => handleSort('lines')}
                    >
                      Lines{sortIndicator('lines')}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="sort-header"
                      onClick={(): void => handleSort('statements')}
                    >
                      Statements{sortIndicator('statements')}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="sort-header"
                      onClick={(): void => handleSort('functions')}
                    >
                      Functions{sortIndicator('functions')}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="sort-header"
                      onClick={(): void => handleSort('branches')}
                    >
                      Branches{sortIndicator('branches')}
                    </button>
                  </th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedServices.map((service) => (
                  <tr key={service.service} className={service.metrics === null ? 'coverage-row--no-data' : ''}>
                    <td className="coverage-service-name">{service.displayName}</td>
                    {service.metrics !== null ? (
                      <>
                        <CoverageCell pct={service.metrics.lines.pct} />
                        <CoverageCell pct={service.metrics.statements.pct} />
                        <CoverageCell pct={service.metrics.functions.pct} />
                        <CoverageCell pct={service.metrics.branches.pct} />
                      </>
                    ) : (
                      <td colSpan={4} className="coverage-no-data">
                        {service.error !== undefined ? service.error : 'No coverage data available'}
                      </td>
                    )}
                    <td className="coverage-last-updated">
                      {service.lastUpdated !== null ? formatWhen(service.lastUpdated) : '—'}
                    </td>
                    <td className="coverage-actions">
                      {service.reportUrl !== null ? (
                        <a
                          href={service.reportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="coverage-view-report"
                        >
                          View Report
                        </a>
                      ) : (
                        <span className="coverage-no-report">No report</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sortedServices.length === 0 && (
            <p className="empty-state" role="status">
              No coverage data available for any services.
            </p>
          )}
        </>
      )}
    </div>
  );
}
